import React, { useEffect } from "react";
import { PFXCriteriaForOrder, PFXItemForOrder, PFXOrderFulfillmentType } from "../models/Interfaces";
import { reactLocalStorage } from "reactjs-localstorage";
import sha1 from "crypto-js/sha1";
import {
  IDeliveryCheckoutInfo,
  selectCleverlynkId,
  selectDeliveryCheckoutInfo,
  selectDeliveryType,
  selectIdentification,
  selectItemsInCart,
  selectLoadInitialDataFromId,
  selectMandatoryCriteria,
  selectOptionalCriteria,
} from "../redux/slices/cleverlynkSlice";
import { useSelector } from "react-redux";
import useCleverlynkFunctions from "./useCleverlynkFunctions";
import useUserFormFunctions from "./useUserFormFunctions";
import { debounce } from "lodash";
import { useLocation } from "react-router";
import { QueryState, selectDeliveryInfo } from "../redux/slices/mapServiceSlice";
import useMapService from "./MapServiceHooks/useMapService";
import { useCleverlynkContext } from "../contexts/CleverlynkContext";
import { useHistory } from "react-router-dom";
import { DELIVERY } from "../pages/UserForm";
import { ALIMENTEC_LIST } from "../components/UserForm/IdentificationField";

const newDate = new Date().getTime();
const ONE_HOUR_FROM_NOW = new Date(newDate + 20 * 60 * 1000).getTime();

export function useLocalStorage() {
  const cleverlynkContext = useCleverlynkContext();
  const cleverlynkId = useSelector(selectCleverlynkId);
  const items = useSelector(selectItemsInCart);
  const identification = useSelector(selectIdentification);
  const mandatoryCriteria = useSelector(selectMandatoryCriteria);
  const optionalCriteria = useSelector(selectOptionalCriteria);
  const deliveryCheckoutInfo = useSelector(selectDeliveryCheckoutInfo);
  const deliveryInfo = useSelector(selectDeliveryInfo);
  const deliveryType = useSelector(selectDeliveryType);
  const loadInitialDataFromIdSupervisor = useSelector(selectLoadInitialDataFromId);

  const location = useLocation();
  const history = useHistory();

  const {
    handleModifyItemsInCart,
    handleModifyLoadedLocalStorage,
    handleSetCleverlynkStage,
    handleModifyDeliveryCheckoutInfo,
  } = useCleverlynkFunctions();

  const {
    handleModifyIdentification,
    handleModifyManyMandatoryCriteria,
    handleModifyDeliveryType,
    handleModifyManyOptionalCriteria,
  } = useUserFormFunctions();

  const { handleModifyDeliveryInfo } = useMapService();

  useEffect(() => {
    if (!cleverlynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
    const init = { expirationDate: ONE_HOUR_FROM_NOW, value: undefined, cleverlynkId: cleverlynkId };

    if (location.pathname.endsWith("/purchase")) {
      handleSetCleverlynkStage(3);
    } else if (location.pathname.endsWith("/info")) {
      handleSetCleverlynkStage(2);
    } else if (location.pathname.includes("/checkout?")) {
      if (location.pathname.endsWith("start=true")) {
        handleSetCleverlynkStage(1);
      } else {
        handleSetCleverlynkStage(2);
      }
    } else if (loadInitialDataFromIdSupervisor.status === "idle") {
      handleSetCleverlynkStage(1);
    }

    let itemsLS = reactLocalStorage.getObject(`items-${cleverlynkId}`);
    const validatorHash: string = String(sha1("cleverlynk" + JSON.stringify(itemsLS.value)));
    if (itemsLS.expirationDate >= newDate && itemsLS.hash === validatorHash && itemsLS.value.length > 0) {
      handleModifyItemsInCart(itemsLS.value);
    } else {
      reactLocalStorage.remove(`items-${cleverlynkId}`);
      // handleSetCleverlynkStage(1, loadInitialDataFromIdSupervisor.status === "idle");
      // let path = `${cleverlynkContext?.basePath}`;
      // history.push({
      //   pathname: path.replace("//", "/"),
      // });
    }

    let identificationLS = reactLocalStorage.getObject(`identification-${cleverlynkId}`, { ...init, value: "" }, true);
    let mandatoryCriteriaLS = reactLocalStorage.getObject(
      "mandatoryCriteria-" + cleverlynkId,
      { ...init, value: [] },
      true
    );
    let optionalCriteriaLS = reactLocalStorage.getObject(
      "optionalCriteria-" + cleverlynkId,
      { ...init, value: [] },
      true
    );
    let deliveryTypeLS = reactLocalStorage.getObject("deliveryType-" + cleverlynkId, { ...init }, true);

    if (identificationLS.expirationDate < newDate) {
      reactLocalStorage.remove(`identification-${cleverlynkId}`);
      identificationLS.value = "";
    }
    if (identificationLS.value && identificationLS.value !== "") {
      handleModifyIdentification(identificationLS.value);
    }

    if (mandatoryCriteriaLS.expirationDate < newDate) {
      reactLocalStorage.remove("mandatoryCriteria-" + cleverlynkId);
      mandatoryCriteriaLS.value = [] as PFXCriteriaForOrder[];
    }
    handleModifyManyMandatoryCriteria(mandatoryCriteriaLS.value);

    if (optionalCriteriaLS.expirationDate < newDate) {
      reactLocalStorage.remove("optionalCriteria-" + cleverlynkId);
      optionalCriteriaLS.value = [] as PFXCriteriaForOrder[];
    }
    handleModifyManyOptionalCriteria(optionalCriteriaLS.value);

    let deliveryCheckoutInfoLS = reactLocalStorage.getObject("deliveryCheckoutInfo-" + cleverlynkId, { ...init }, true);
    if (deliveryCheckoutInfoLS.expirationDate < newDate) {
      reactLocalStorage.remove("deliveryCheckoutInfo-" + cleverlynkId);
      deliveryCheckoutInfoLS.value = undefined;
    }
    handleModifyDeliveryCheckoutInfo(deliveryCheckoutInfoLS.value);

    let deliveryInfoLS = reactLocalStorage.getObject("deliveryInfo-" + cleverlynkId, { ...init }, true);
    if (deliveryInfoLS.expirationDate < newDate) {
      reactLocalStorage.remove("deliveryInfo-" + cleverlynkId);
      deliveryInfoLS.value = undefined;
    }
    if (!deliveryInfo?.address || deliveryInfo?.address?.length === 0) {
      handleModifyDeliveryInfo(deliveryInfoLS.value);
    }

    if (deliveryTypeLS.expirationDate < newDate) {
      reactLocalStorage.remove("deliveryType-" + cleverlynkId);
      deliveryTypeLS.value = "";
    }
    if (deliveryTypeLS.value && deliveryTypeLS.value !== "") {
      if (deliveryTypeLS.value === "Domicilio") handleModifyDeliveryType(PFXOrderFulfillmentType.DELIVERY);
      else if (deliveryTypeLS.value === "TakeOut") handleModifyDeliveryType(PFXOrderFulfillmentType.TAKEOUT);
      else handleModifyDeliveryType(deliveryTypeLS.value);
    }
    handleModifyLoadedLocalStorage(true);
  }, [cleverlynkId]);

  // const modifyItems

  const modifyItemsLS = React.useRef((itms: PFXItemForOrder[], clynkId: string | undefined) => {
    if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
    reactLocalStorage.remove("items-" + clynkId);
    let itemsLS = reactLocalStorage.getObject("items-" + clynkId);
    itemsLS.value = itms;
    itemsLS.expirationDate = ONE_HOUR_FROM_NOW;
    const hashValidator: string = String(sha1("cleverlynk" + JSON.stringify(itemsLS.value)));
    itemsLS.hash = hashValidator;
    reactLocalStorage.setObject("items-" + clynkId, itemsLS);
  }).current;

  const modifyIdentificationLS = React.useRef(
    debounce((id: string, clynkId: string | undefined) => {
      if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
      reactLocalStorage.remove("identification-" + clynkId);
      let identificationLS = reactLocalStorage.getObject("identification-" + clynkId);
      identificationLS.value = id;
      identificationLS.expirationDate = ONE_HOUR_FROM_NOW;
      reactLocalStorage.setObject("identification-" + clynkId, identificationLS);
    }, 1500)
  ).current;

  const modifyMandatoryCriteriaLS = React.useRef(
    debounce((mc: PFXCriteriaForOrder[], clynkId: string | undefined) => {
      if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
      reactLocalStorage.remove("mandatoryCriteria-" + clynkId);
      let mandatoryCriteriaLS = reactLocalStorage.getObject("mandatoryCriteria-" + clynkId);
      mandatoryCriteriaLS.value = mc;
      mandatoryCriteriaLS.expirationDate = ONE_HOUR_FROM_NOW;
      reactLocalStorage.setObject("mandatoryCriteria-" + clynkId, mandatoryCriteriaLS);
    }, 1500)
  ).current;

  const modifyOptionalCriteriaLS = React.useRef(
    debounce((oc: PFXCriteriaForOrder[], clynkId: string | undefined) => {
      if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
      reactLocalStorage.remove("optionalCriteria-" + clynkId);
      let optionalCriteriaLS = reactLocalStorage.getObject("optionalCriteria-" + clynkId);
      optionalCriteriaLS.value = oc;
      optionalCriteriaLS.expirationDate = ONE_HOUR_FROM_NOW;
      reactLocalStorage.setObject("optionalCriteria-" + clynkId, optionalCriteriaLS);
    }, 1500)
  ).current;

  const modifyDeliveryCheckoutInfoLS = React.useRef(
    (dgi: IDeliveryCheckoutInfo | undefined, clynkId: string | undefined) => {
      if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
      reactLocalStorage.remove("deliveryCheckoutInfo-" + clynkId);
      let deliveryCheckoutInfoLS = reactLocalStorage.getObject("deliveryCheckoutInfo-" + clynkId);
      deliveryCheckoutInfoLS.value = dgi;
      deliveryCheckoutInfoLS.expirationDate = ONE_HOUR_FROM_NOW;
      reactLocalStorage.setObject("deliveryCheckoutInfo-" + clynkId, deliveryCheckoutInfoLS);
    }
  ).current;

  const modifyDeliveryInfoLS = React.useRef((dgi: QueryState | undefined, clynkId: string | undefined) => {
    if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
    reactLocalStorage.remove("deliveryInfo-" + clynkId);
    let deliveryInfoLS = reactLocalStorage.getObject("deliveryInfo-" + clynkId);
    deliveryInfoLS.value = dgi;
    deliveryInfoLS.expirationDate = ONE_HOUR_FROM_NOW;
    if (dgi && dgi.address && dgi.address !== "") {
      reactLocalStorage.setObject("deliveryInfo-" + clynkId, deliveryInfoLS);
      handleModifyDeliveryType(PFXOrderFulfillmentType.DELIVERY);
    }
  }).current;

  const modifyDeliveryTypeLS = React.useRef(
    (type: PFXOrderFulfillmentType | undefined, clynkId: string | undefined) => {
      if (!clynkId || ALIMENTEC_LIST.includes(cleverlynkId ?? "")) return;
      reactLocalStorage.remove("deliveryType-" + clynkId);
      let deliveryTypeLS = reactLocalStorage.getObject("deliveryType-" + clynkId);
      deliveryTypeLS.value = type;
      deliveryTypeLS.expirationDate = ONE_HOUR_FROM_NOW;
      reactLocalStorage.setObject("deliveryType-" + clynkId, deliveryTypeLS);
    }
  ).current;

  useEffect(() => modifyItemsLS(items, cleverlynkId), [items, cleverlynkId]);

  useEffect(
    () => modifyIdentificationLS(identification, cleverlynkId),
    [identification, cleverlynkId, modifyIdentificationLS]
  );

  useEffect(
    () => modifyMandatoryCriteriaLS(mandatoryCriteria, cleverlynkId),
    [mandatoryCriteria, cleverlynkId, modifyMandatoryCriteriaLS]
  );

  useEffect(
    () => modifyOptionalCriteriaLS(optionalCriteria, cleverlynkId),
    [optionalCriteria, cleverlynkId, modifyOptionalCriteriaLS]
  );

  useEffect(
    () => modifyDeliveryCheckoutInfoLS(deliveryCheckoutInfo, cleverlynkId),
    [modifyDeliveryCheckoutInfoLS, deliveryCheckoutInfo, cleverlynkId]
  );

  useEffect(() => modifyDeliveryInfoLS(deliveryInfo, cleverlynkId), [modifyDeliveryInfoLS, deliveryInfo, cleverlynkId]);

  useEffect(() => modifyDeliveryTypeLS(deliveryType, cleverlynkId), [deliveryType, cleverlynkId, modifyDeliveryTypeLS]);
}
